import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import { Field, Input, Select } from "./ui"
import { validateAddress, filterAddressInput, addressInputMode } from "aldoo-ra/Store/address-validation"

// the countries the shop delivers to (SUPPORTED_COUNTRIES of the address rules)
export const COUNTRIES = [
  ["BG", "Bulgaria"],
  ["AT", "Austria"],
  ["BE", "Belgium"],
  ["HR", "Croatia"],
  ["CY", "Cyprus"],
  ["CZ", "Czechia"],
  ["DK", "Denmark"],
  ["EE", "Estonia"],
  ["FI", "Finland"],
  ["FR", "France"],
  ["DE", "Germany"],
  ["GR", "Greece"],
  ["HU", "Hungary"],
  ["IE", "Ireland"],
  ["IT", "Italy"],
  ["LV", "Latvia"],
  ["LT", "Lithuania"],
  ["LU", "Luxembourg"],
  ["MT", "Malta"],
  ["NL", "Netherlands"],
  ["PL", "Poland"],
  ["PT", "Portugal"],
  ["RO", "Romania"],
  ["SK", "Slovakia"],
  ["SI", "Slovenia"],
  ["ES", "Spain"],
  ["SE", "Sweden"],
  ["GB", "United Kingdom"],
  ["US", "United States"],
]

export const EMPTY_ADDRESS = { name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "BG", phone: "" }

// the rule codes of aldoo-ra/Store/address-validation, in the site's words
export function addressMessages(t) {
  return {
    name_required: t`Please enter the recipient name`,
    name_invalid: t`Enter a real name: first and last name, letters only`,
    line1_required: t`Please enter the street address`,
    line1_invalid: t`Enter a real street address: street, number, block`,
    line1_number: t`Add the street number or block`,
    line2_invalid: t`This line has characters an address does not use`,
    city_required: t`Please enter the city`,
    city_invalid: t`Enter a real city or village name`,
    state_invalid: t`Enter a real region name`,
    postal_code_required: t`Please enter the postal code`,
    postal_code_invalid: t`This is not a postal code of the selected country`,
    country_required: t`Please select a country`,
    country_invalid: t`Select a country from the list`,
    phone_required: t`Please enter a phone number`,
    phone_invalid: t`Enter a real phone number, e.g. +359 88 123 4567`,
  }
}

export function addressErrors(address, t) {
  const messages = addressMessages(t)
  return Object.fromEntries(Object.entries(validateAddress(address)).map(([field, code]) => [field, messages[code] || code]))
}

export const addressLine = (a) => [a.name, a.line1, a.line2, a.city, a.postal_code, a.country, a.phone].filter(Boolean).join(", ")

/** Shared billing/shipping address form (account + checkout). */
export default function AddressForm({ initial = EMPTY_ADDRESS, onSubmit, onCancel, submitLabel, busy = false, children, hideActions = false, formId, onChange }) {
  const t = useT()
  // a saved address comes with null for the fields it has no value in; the
  // inputs are controlled, so those are empty strings here
  const [address, setAddress] = useState(() => {
    const given = Object.fromEntries(Object.entries(initial || {}).filter(([, v]) => v !== null && v !== undefined))
    return { ...EMPTY_ADDRESS, ...given }
  })
  // an address given to fix (a saved one that fails the rules) shows why at once
  const [errors, setErrors] = useState(() => (initial?.line1 || initial?.phone ? addressErrors({ ...EMPTY_ADDRESS, ...initial }, t) : {}))
  // what a field does not accept never gets into it
  const set = (key) => (e) =>
    setAddress((a) => {
      const next = { ...a, [key]: key === "country" ? e.target.value : filterAddressInput(key, e.target.value, a.country) }
      onChange?.(next)
      return next
    })
  // a field that was filled is checked when it is left; one that had an error, as it is typed
  const check = (key) => () => {
    if (!String(address[key] ?? "").trim() && !errors[key]) return
    const found = addressErrors(address, t)
    setErrors((prev) => {
      const next = { ...prev }
      if (found[key]) next[key] = found[key]
      else delete next[key]
      return next
    })
  }
  const field = (key, extra = {}) => ({
    name: key,
    value: address[key],
    onChange: set(key),
    onBlur: check(key),
    inputMode: addressInputMode(key, address.country),
    "aria-invalid": errors[key] ? true : undefined,
    ...extra,
  })

  return (
    <form
      id={formId}
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="address-form"
      onSubmit={(e) => {
        e.preventDefault()
        const next = addressErrors(address, t)
        setErrors(next)
        if (Object.keys(next).length) return
        onSubmit(address)
      }}
    >
      <Field label={t`Full name`} error={errors.name} className="md:col-span-2">
        <Input {...field("name", { autoComplete: "name" })} />
      </Field>
      <Field label={t`Address`} error={errors.line1} className="md:col-span-2">
        <Input {...field("line1", { autoComplete: "address-line1", placeholder: t`Street, number, block, entrance, apartment` })} />
      </Field>
      <Field label={t`Address line 2`} error={errors.line2} className="md:col-span-2">
        <Input {...field("line2", { autoComplete: "address-line2" })} />
      </Field>
      <Field label={t`City`} error={errors.city}>
        <Input {...field("city", { autoComplete: "address-level2" })} />
      </Field>
      <Field label={t`State / Province`} error={errors.state}>
        <Input {...field("state", { autoComplete: "address-level1" })} />
      </Field>
      <Field label={t`Postal code`} error={errors.postal_code}>
        <Input {...field("postal_code", { autoComplete: "postal-code" })} />
      </Field>
      <Field label={t`Country`} error={errors.country}>
        <Select name="country" value={address.country} onChange={set("country")} autoComplete="country">
          {COUNTRIES.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t`Phone`} error={errors.phone}>
        <Input {...field("phone", { autoComplete: "tel", type: "tel", placeholder: "+359 88 123 4567" })} />
      </Field>
      {children}
      {hideActions && <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />}
      {!hideActions && (
      <div className="md:col-span-2 flex flex-row flex-wrap gap-3 justify-end">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t`Cancel`}
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={busy} data-testid="address-submit">
          {submitLabel || t`Save`}
        </Button>
      </div>
      )}
    </form>
  )
}
